import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { fileUrl, apiErr } from "@/lib/api";
import { useI18n, catName } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Upload, ImageIcon, FileUp, Check, Images, X } from "lucide-react";

export default function ProductForm() {
  const { t, lang } = useI18n();
  const { user, ready } = useAuth();
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const imgRef = useRef();
  const fileRef = useRef();
  const galleryRef = useRef();

  const [categories, setCategories] = useState([]);
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiFields, setAiFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", bullets: "", price: "", category: "", tags: "",
    preview_image_url: "", gallery: [], file_id: null, file_name: "",
  });

  useEffect(() => {
    if (ready && user === false) navigate("/login");
  }, [ready, user, navigate]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (editing) {
      api.get(`/products/${id}`).then((r) => {
        const p = r.data;
        setForm({
          title: p.title, description: p.description, bullets: (p.bullets || []).join("\n"),
          price: String(p.price), category: p.category, tags: (p.tags || []).join(", "),
          preview_image_url: p.preview_image_url, gallery: p.gallery || [],
          file_id: p.file_id, file_name: p.file_id ? "archivo actual" : "",
        });
      }).catch(() => {});
    }
  }, [id, editing]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    if (notes.trim().length < 3) { toast.error("Escribe unas notas primero"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-listing", { notes, lang });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
        bullets: (data.bullets || []).join("\n"),
        tags: (data.tags || []).join(", "),
        category: data.category || f.category,
      }));
      setAiFields({ title: true, description: true, bullets: true, tags: true, category: true });
      toast.success(t("form.aiSuggested"));
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "AI error");
    } finally {
      setGenerating(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, preview_image_url: data.url }));
      toast.success("Imagen subida");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploadingImg(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_id: data.file_id, file_name: data.filename }));
      toast.success("Archivo subido");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploadingFile(false); }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, gallery: [...f.gallery, ...urls] }));
      toast.success("Galería actualizada");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploadingGallery(false); }
  };

  const removeGalleryImg = (idx) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title, description: form.description,
      bullets: form.bullets.split("\n").map((b) => b.trim()).filter(Boolean),
      price: parseFloat(form.price || "0"),
      category: form.category,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      preview_image_url: form.preview_image_url,
      gallery: form.gallery,
      file_id: form.file_id,
    };
    try {
      if (editing) await api.put(`/products/${id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Guardado" : t("form.pendingMsg"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const AiBadge = ({ show }) => show ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0EA5E9] bg-sky-50 rounded-full px-2 py-0.5 ml-2">
      <Sparkles className="h-2.5 w-2.5" /> AI
    </span>
  ) : null;

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-8">
        {editing ? t("form.editTitle") : t("form.createTitle")}
      </h1>

      {!editing && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="tracing-beam rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-[#0EA5E9]" />
            <h2 className="font-display font-bold text-[#0F172A]">{t("form.notes")}</h2>
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder={t("form.notesPlaceholder")} data-testid="ai-notes" className="rounded-xl resize-none" />
          <Button onClick={generate} disabled={generating} type="button" data-testid="ai-generate-btn"
            className="mt-3 rounded-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold">
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {generating ? t("form.generating") : t("form.generate")}
          </Button>
        </motion.div>
      )}

      <form onSubmit={submit} className="space-y-5 bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <div>
          <Label>{t("form.pTitle")}<AiBadge show={aiFields.title} /></Label>
          <Input value={form.title} onChange={set("title")} required data-testid="form-title" className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label>{t("form.pDescription")}<AiBadge show={aiFields.description} /></Label>
          <Textarea value={form.description} onChange={set("description")} rows={5} data-testid="form-description" className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label>{t("form.pBullets")}<AiBadge show={aiFields.bullets} /></Label>
          <Textarea value={form.bullets} onChange={set("bullets")} rows={5} data-testid="form-bullets" className="mt-1.5 rounded-xl" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>{t("form.pPrice")}</Label>
            <Input type="number" step="0.01" min="0" value={form.price} onChange={set("price")} required data-testid="form-price" className="mt-1.5 rounded-xl" />
          </div>
          <div>
            <Label>{t("form.pCategory")}<AiBadge show={aiFields.category} /></Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="form-category"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{catName(c, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>{t("form.pTags")}<AiBadge show={aiFields.tags} /></Label>
          <Input value={form.tags} onChange={set("tags")} data-testid="form-tags" className="mt-1.5 rounded-xl" placeholder="figma, ui, dashboard" />
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>{t("form.pImage")}</Label>
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={uploadImage} data-testid="form-image-input" />
            <div onClick={() => imgRef.current?.click()}
              className="mt-1.5 cursor-pointer border-2 border-dashed border-[#E2E8F0] rounded-xl p-4 text-center hover:border-[#0EA5E9] transition-colors">
              {form.preview_image_url ? (
                <img src={fileUrl(form.preview_image_url)} alt="" className="h-24 mx-auto rounded-lg object-cover" />
              ) : (
                <div className="text-[#94A3B8] py-4">
                  {uploadingImg ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : <ImageIcon className="h-6 w-6 mx-auto mb-1" />}
                  <span className="text-sm">{t("form.uploadImage")}</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label>{t("form.pFile")}</Label>
            <input ref={fileRef} type="file" hidden onChange={uploadFile} data-testid="form-file-input" />
            <div onClick={() => fileRef.current?.click()}
              className="mt-1.5 cursor-pointer border-2 border-dashed border-[#E2E8F0] rounded-xl p-4 text-center hover:border-[#0EA5E9] transition-colors h-[calc(100%-2rem)] flex items-center justify-center">
              {form.file_name ? (
                <div className="text-[#0EA5E9] flex items-center gap-2"><Check className="h-5 w-5" /><span className="text-sm truncate">{form.file_name}</span></div>
              ) : (
                <div className="text-[#94A3B8]">
                  {uploadingFile ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : <FileUp className="h-6 w-6 mx-auto mb-1" />}
                  <span className="text-sm">{t("form.uploadFile")}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <Label>{t("form.pGallery")}</Label>
          <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={uploadGallery} data-testid="form-gallery-input" />
          <div className="mt-1.5 flex flex-wrap gap-3">
            {form.gallery.map((g, i) => (
              <div key={i} className="relative h-20 w-24 rounded-xl overflow-hidden border border-[#E2E8F0] group" data-testid={`gallery-item-${i}`}>
                <img src={fileUrl(g)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => removeGalleryImg(i)} data-testid={`gallery-remove-${i}`}
                  className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => galleryRef.current?.click()} data-testid="form-gallery-add"
              className="h-20 w-24 rounded-xl border-2 border-dashed border-[#E2E8F0] flex items-center justify-center text-[#94A3B8] hover:border-[#0EA5E9] transition-colors">
              {uploadingGallery ? <Loader2 className="h-5 w-5 animate-spin" /> : <Images className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="form-submit"
          className="w-full rounded-full h-12 bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold text-base">
          {saving ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Upload className="h-5 w-5 mr-2" />}
          {editing ? t("form.save") : t("form.publish")}
        </Button>
      </form>
    </div>
  );
}
